import { useEffect } from "react";
import {
  Box,
  List,
  ListItemButton,
  ListItemText,
  Button,
  Typography,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import ChatBubbleOutlineIcon from "@mui/icons-material/ChatBubbleOutline";
import { useSessionHistory, useSessionHistoryLoaded, useActiveSessionId } from "../../slices/ui";
import { useListSessions, useOpenChat } from "../../hook/ai-panel";
import { attentionColor, attentionLabel, rowSubtitle, rowTitle } from "./attention";
import { useNow } from "../../hook/useNow";
import { CasLoader } from "../brand/CasLoader";
import { byRecency, containerLabel, heldLabel } from "./sessionPins";

interface Props {
  open: boolean;
  onNewChat: () => void;
  // The Phone Shell's Chats view: the list fills the screen instead of a 248 px rail.
  fullWidth?: boolean;
  // Called after a row or New chat is chosen (the Phone Shell then shows Cas).
  onPicked?: () => void;
}

const OPEN_WIDTH = 248;
const SESSION_LIST_REFRESH_MS = 60_000;
const CHIP_MAX_WIDTH = 70;
const CHIP_FONT_SIZE = 10;

export const SessionHistorySidebar = ({ open, onNewChat, fullWidth, onPicked }: Props) => {
  const now = useNow(60_000);
  const sessions = byRecency(useSessionHistory());
  const loaded = useSessionHistoryLoaded();
  const activeId = useActiveSessionId();
  const resume = useOpenChat();
  const refresh = useListSessions();
  // The chat list is where the user sees which Session needs them (cas-commander): it is re-requested every minute while
  // mounted, so a Session that blocked or finished since the last Run shows without a reload.
  useEffect(() => {
    const t = setInterval(refresh, SESSION_LIST_REFRESH_MS);
    return () => clearInterval(t);
  }, [refresh]);

  return (
    <Box
      data-testid="session-history"
      sx={{
        width: fullWidth ? "100%" : open ? OPEN_WIDTH : 0,
        flexShrink: 0,
        // In the Phone Shell the rail sits in a flex column (PhoneShell.tsx pane()): it must fill the pane, not grow to every row,
        // or the pane clips the list and nothing scrolls (real-component experiment 2026-10-26: last chat at 3023 px on a 700 px screen).
        ...(fullWidth ? { flex: 1, minHeight: 0 } : {}),
        borderRight: open ? 1 : 0,
        borderColor: "divider",
        bgcolor: "action.hover",
        display: "flex",
        flexDirection: "column",
        overflow: "hidden",
        transition: "width 0.2s ease",
      }}
    >
      {/* Fixed-width inner keeps content from reflowing while the rail animates. */}
      <Box sx={{ width: fullWidth ? "100%" : OPEN_WIDTH, display: "flex", flexDirection: "column", flex: 1, minHeight: 0 }}>
        <Box sx={{ p: 1.25, pb: 1 }}>
          <Button
            data-testid="new-chat"
            onClick={() => { onNewChat(); onPicked?.(); }}
            fullWidth
            disableElevation
            variant="contained"
            size="small"
            startIcon={<AddIcon sx={{ fontSize: 16 }} />}
            sx={{ textTransform: "none", fontWeight: 600, borderRadius: 1.5, py: 0.6 }}
          >
            New chat
          </Button>
        </Box>

        <Typography
          variant="caption"
          sx={{
            px: 1.75,
            pb: 0.5,
            color: "text.secondary",
            textTransform: "uppercase",
            letterSpacing: 0.6,
            fontSize: 10,
            fontWeight: 700,
          }}
        >
          Conversations
        </Typography>

        {sessions.length === 0 && !loaded ? (
          <Box data-testid="session-history-loading" sx={{ px: 2, py: 3, color: "text.disabled", textAlign: "center" }}>
            <CasLoader size={16} label="Loading conversations" />
            <Typography variant="caption" sx={{ display: "block", lineHeight: 1.4, mt: 0.5 }}>
              Loading conversations
            </Typography>
          </Box>
        ) : sessions.length === 0 ? (
          <Box sx={{ px: 2, py: 3, color: "text.disabled", textAlign: "center" }}>
            <ChatBubbleOutlineIcon sx={{ fontSize: 26, opacity: 0.5, mb: 0.5 }} />
            <Typography variant="caption" sx={{ display: "block", lineHeight: 1.4 }}>
              No conversations yet.<br />Start one with “New chat”.
            </Typography>
          </Box>
        ) : (
          <List dense disablePadding sx={{ overflowY: "auto", flex: 1, px: 0.75, py: 0.5 }}>
            {sessions.map((s) => {
              const id = s["session-id"];
              const selected = id === activeId;
              const held = heldLabel(s);
              const chip = containerLabel(s);
              return (
                <ListItemButton
                  key={id}
                  data-testid={`session-row-${id}`}
                  data-hot={s.podBound ? "true" : undefined}
                  selected={selected}
                  onClick={() => { resume(id); onPicked?.(); }}
                  sx={{
                    borderRadius: 1.5,
                    mb: 0.25,
                    py: 0.6,
                    pl: 1.25,
                    position: "relative",
                    flexWrap: "wrap",
                    "&::before": {
                      content: '""',
                      position: "absolute",
                      left: 4,
                      top: 8,
                      bottom: 8,
                      width: 3,
                      borderRadius: 2,
                      bgcolor: "primary.main",
                      opacity: selected ? 1 : 0,
                      transition: "opacity 0.15s ease",
                    },
                    "&.Mui-selected": { bgcolor: "action.selected" },
                    "&.Mui-selected:hover": { bgcolor: "action.selected" },
                  }}
                >
                  <ChatBubbleOutlineIcon
                    sx={{
                      fontSize: 15,
                      mr: 1,
                      color: selected ? "primary.main" : "text.disabled",
                      flexShrink: 0,
                    }}
                  />
                  <ListItemText
                    data-testid={`session-title-${id}`}
                    primary={rowTitle(s, now)}
                    secondary={rowSubtitle(s, now)}
                    primaryTypographyProps={{
                      fontSize: 13,
                      fontWeight: selected ? 600 : 500,
                      noWrap: true,
                    }}
                    secondaryTypographyProps={{
                      fontSize: 11,
                      fontFamily: "ui-monospace, SFMono-Regular, Menlo, monospace",
                      sx: { opacity: 0.65 },
                      noWrap: true,
                    }}
                  />
                {attentionLabel(s.attention) ? (
                    <Typography
                      data-testid={`session-attention-${id}`}
                      variant="caption"
                      color={`${attentionColor(s.attention)}.main`}
                      sx={{ ml: 1, flexShrink: 0, fontWeight: 600, fontSize: 10, textTransform: "uppercase", letterSpacing: 0.4 }}
                    >
                      {attentionLabel(s.attention)}
                    </Typography>
                  ) : null}
                  {s.podBound && chip ? (
                    <Typography
                      data-testid={`session-pod-${id}`}
                      variant="caption"
                      color="text.secondary"
                      sx={{ ml: 1, flexShrink: 0, maxWidth: CHIP_MAX_WIDTH, fontSize: CHIP_FONT_SIZE, border: 1, borderColor: "divider", borderRadius: 1, px: 0.5 }}
                      noWrap
                    >
                      {chip}
                    </Typography>
                  ) : null}
                  {held ? (
                    <Typography
                      data-testid={`session-held-${id}`}
                      variant="caption"
                      color="warning.main"
                      sx={{ width: "100%", pl: 3, fontSize: 10, lineHeight: 1.3 }}
                    >
                      {held}
                    </Typography>
                  ) : null}
                </ListItemButton>
              );
            })}
          </List>
        )}
      </Box>
    </Box>
  );
};
